import { useMemo, useState } from "react"
import {Calendar, Clock, ExternalLink, Filter, Tag, TrendingUp, User} from 'lucide-react'
import { Link } from "react-router-dom"
import {
  HEALTH_CATEGORIES,
  allBlogPosts,
  blogPosts,
  featuredPost,
  popularTopics,
  type HealthCategory,
} from "@/data/blog"

function postMatchesFilters(
  post: (typeof allBlogPosts)[number],
  selectedCategory: HealthCategory | "All",
  selectedTags: string[],
): boolean {
  const matchesCategory =
    selectedCategory === "All" || post.categories.includes(selectedCategory)
  const matchesTags =
    selectedTags.length === 0 || selectedTags.some((tag) => post.tags.includes(tag))
  return matchesCategory && matchesTags
}

function Blog() {
  const [selectedCategory, setSelectedCategory] = useState<HealthCategory | "All">("All")
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    allBlogPosts.forEach((post) => {
      post.tags.forEach((tag) => tagSet.add(tag))
    })
    return Array.from(tagSet)
  }, [])

  const showFeatured = postMatchesFilters(featuredPost, selectedCategory, selectedTags)

  const filteredPosts = useMemo(
    () => blogPosts.filter((post) => postMatchesFilters(post, selectedCategory, selectedTags)),
    [selectedCategory, selectedTags],
  )

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag],
    )
  }

  const clearFilters = () => {
    setSelectedCategory("All")
    setSelectedTags([])
  }

  const hasActiveFilters = selectedCategory !== "All" || selectedTags.length > 0

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50">
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4">Vitamin Vital Blog</h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Evidence-based insights on vitamins, supplements, and optimizing your health
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {showFeatured ? (
          <div className="mb-16">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden hover:shadow-3xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="relative h-64 md:h-auto">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-orange-500 text-white px-4 py-2 rounded-full font-semibold text-sm">
                    Featured
                  </div>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 text-teal-600 text-sm font-medium mb-3">
                    <TrendingUp className="w-4 h-4" />
                    {featuredPost.editorialCategory}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {featuredPost.categories.map((category) => (
                      <button
                        key={category}
                        type="button"
                        onClick={() => setSelectedCategory(category)}
                        className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-xs font-semibold"
                      >
                        {category}
                      </button>
                    ))}
                    {featuredPost.tags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {featuredPost.author}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {featuredPost.date}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime}
                    </div>
                  </div>
                  <Link
                    to={`/blog/${featuredPost.slug}`}
                    className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-full font-semibold transition-colors w-fit"
                  >
                    Read Full Article
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-teal-700" />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-teal-800">
              Categories
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setSelectedCategory("All")}
              className={`px-6 py-2 rounded-full font-medium transition-colors ${
                selectedCategory === "All"
                  ? "bg-teal-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              All Posts
            </button>
            {HEALTH_CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === category
                    ? "bg-teal-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-orange-600" />
              <h2 className="text-sm font-semibold uppercase tracking-wide text-orange-800">
                Tags
              </h2>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm font-medium text-teal-700 hover:text-teal-900"
              >
                Clear filters
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {allTags.map((tag) => {
              const isActive = selectedTags.includes(tag)
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-orange-500 text-white"
                      : "bg-white text-gray-700 hover:bg-orange-50"
                  }`}
                >
                  {tag}
                </button>
              )
            })}
          </div>
        </div>

        {filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredPosts.map((post) => (
              <article
                key={post.slug}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow"
              >
                <div className="relative h-48">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {post.editorialCategory}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {post.categories.map((category) => (
                      <span
                        key={`${post.slug}-${category}`}
                        className="bg-teal-50 text-teal-700 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 hover:text-teal-600 transition-colors">
                    <Link to={`/blog/${post.slug}`}>
                      {post.title}
                    </Link>
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag) => (
                      <button
                        key={`${post.slug}-tag-${tag}`}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          selectedTags.includes(tag)
                            ? "bg-orange-500 text-white"
                            : "bg-orange-50 text-orange-700 hover:bg-orange-100"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {post.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">{post.date}</div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mb-16 bg-white rounded-2xl shadow-lg p-10 text-center">
            <p className="text-gray-700 font-medium mb-2">No articles match these filters.</p>
            <p className="text-sm text-gray-500 mb-6">
              Try another category or clear tags to see more Vitamin Vital Portal articles.
            </p>
            <button
              type="button"
              onClick={clearFilters}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-full font-semibold transition-colors"
            >
              Show all posts
            </button>
          </div>
        )}

        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Never Miss a Health Tip</h2>
          <p className="text-teal-100 mb-6 max-w-2xl mx-auto">
            Get weekly evidence-based insights on vitamins, supplements, and wellness delivered to your inbox.
          </p>
          <div className="max-w-md mx-auto flex gap-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap">
              Subscribe
            </button>
          </div>
          <p className="text-xs text-teal-200 mt-4">
            No spam. Unsubscribe anytime. Your privacy is protected.
          </p>
        </div>

        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Popular Topics</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {popularTopics.map((topic) => {
              const isCategory = HEALTH_CATEGORIES.includes(topic as HealthCategory)
              const isActive = isCategory
                ? selectedCategory === topic
                : selectedTags.includes(topic)
              return (
                <button
                  key={topic}
                  type="button"
                  onClick={() => {
                    if (isCategory) {
                      setSelectedCategory(topic as HealthCategory)
                      return
                    }
                    toggleTag(topic)
                  }}
                  className={`px-4 py-3 rounded-lg text-sm font-medium text-center transition-colors ${
                    isActive
                      ? "bg-teal-600 text-white"
                      : "bg-teal-50 hover:bg-teal-100 text-teal-700"
                  }`}
                >
                  {topic}
                </button>
              )
            })}
          </div>
        </div>

        <div className="mt-12 bg-gray-50 border-l-4 border-teal-600 rounded-r-lg p-6">
          <p className="text-sm text-gray-600 leading-relaxed">
            <strong className="text-gray-900">Medical Disclaimer:</strong> The content on Vitamin Vital Portal is for informational purposes only and should not be considered medical advice. Always consult with a qualified healthcare professional before making changes to your supplement regimen or if you have concerns about your health.
          </p>
        </div>
      </div>
    </div>
  )
}

export default Blog
